import * as Sentry from '@sentry/browser'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import resolveConfig from 'tailwindcss/resolveConfig'

import tailwindConfig from '../../../tailwind.config.js'
import { useQueryHistoryStore, useStrictModeSetting } from '../../store'
import { DQLHTTPResponse } from '../../types'
import { isDataModifyingQuery } from '../../utils/utils'
import { Column } from '../queryResultTable/QueryResultTable'
import { DQLEditorContext } from './context'
import { DQLEditorProps } from './DQLEditor'

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Improperly typed
const tailwind = resolveConfig(tailwindConfig)

const WINDOW_FOCUS_TTL = 1000 * 60 * 5 // 5 minutes

type Props = {
  children: React.ReactNode
} & DQLEditorProps

export default function DQLEditorProvider({
  children,
  accessToken,
  refreshToken,
  executeQuery,
  initialQuery,
  logDataAccessedEvent,
  ...props
}: Props) {
  const { enabled: strictMode } = useStrictModeSetting()

  // Use a separate internal state for setting the query so we can control when the query is executed
  // This maintains backwards compatibility with the old setQuery function
  const [query, setQueryState] = useState(initialQuery)
  const [shouldExecute, setShouldExecute] = useState(true)

  // Store the previous result for data-modifying queries
  const [previousResult, setPreviousResult] = useState<
    DQLHTTPResponse | undefined
  >(undefined)

  // Use a ref for immediate access to the previous result to avoid race conditions
  const previousResultRef = useRef<DQLHTTPResponse | undefined>(undefined)

  // Track query execution time
  const queryStartTimeRef = useRef<number | null>(null)

  // By default, execute the query. If execute is false, the query will not be executed
  const setQuery = (newQuery: string, execute = true) => {
    setShouldExecute(execute)
    setQueryState(newQuery)
  }

  const [sideBarOpen, setSideBarOpen] = useState(true)
  const [historySideBarOpen, setHistorySideBarOpen] = useState(true)

  const [showLatestTour, setShowLatestTour] = useState(false)

  const [activeView, setActiveView] = useState<'collections' | 'editor'>(
    'collections',
  )

  const [activeRow, setActiveRow] = useState<Column | undefined>(undefined)

  const [refetchInterval, setRefetchInterval] = useState<number | false>(false)

  const { addQueryToHistory } = useQueryHistoryStore()

  /**
   * A naive ref to track the queries which have been audited already this
   * session
   */
  const reportedRef = useRef<Record<string, boolean>>({})

  const {
    data,
    isLoading,
    error: serviceError,
    fetchStatus,
    refetch,
  } = useQuery(
    ['dql', query, strictMode, activeView],
    () => {
      queryStartTimeRef.current = performance.now()

      // Record breadcrumb when query is executed
      Sentry.addBreadcrumb({
        category: 'dql-editor',
        message: 'DQL query executed',
        level: 'info',
        data: { query, activeView },
      })

      return executeQuery(query || '', strictMode)
    },
    {
      enabled: !!query && !!accessToken && shouldExecute,
      // Disable default caching behavior. This is because the queries were being cached
      // unexpectedly, and we don't want to rely on the cache for correctness.
      cacheTime: 0,
      // We want to refetch on window focus if the last fetch was more than WINDOW_FOCUS_TTL ago. We don't want to
      // necessarily rely on staleness since a query by default becomes stale immediately. We also don't want to
      // refetch in the editor view, as we don't want to refresh the results on INSERT/UPDATE/DELETE.
      refetchOnWindowFocus: (query) => {
        if (activeView === 'editor') {
          return false
        }

        const { dataUpdatedAt, errorUpdatedAt } = query.state
        // If the last update was an error, the dataUpdatedAt will be 0, so we use the errorUpdatedAt as a fallback.
        const lastUpdate = new Date(dataUpdatedAt || errorUpdatedAt).getTime()
        const now = new Date().getTime()
        const diff = now - lastUpdate
        return diff > WINDOW_FOCUS_TTL
      },
      // Only allow refetching in the collections view. This is not strictly necessary,
      // but having this extra check is a bit more robust.
      refetchInterval: activeView === 'collections' && refetchInterval,
      // Errors should only be thrown for more serious failures, e.g. 5xx errors,
      // from the HTTP API. If we want to report on 4xx errors, or other user-facing
      // errors, we should consider another approach.
      onError: async (error: unknown) => {
        const shouldRetry =
          isAxiosError(error) && error.response?.status === 401
        if (shouldRetry && refreshToken) {
          await refreshToken()
          refetch()
        } else {
          // Record query execution time for failed queries
          if (queryStartTimeRef.current !== null) {
            const executionTime = performance.now() - queryStartTimeRef.current

            Sentry.captureMessage('DQL query failed', {
              level: 'error',
              extra: {
                executionTime,
              },
            })

            queryStartTimeRef.current = null
          }

          Sentry.withScope((scope) => {
            scope.setTag(
              'dql-editor-query-error',
              'The HTTP API returned an error for a DQL query',
            )
            Sentry.captureException(error)
          })
        }
      },

      // Adds the query to the history on success
      onSuccess: (newData) => {
        // Record query execution time to Sentry
        if (queryStartTimeRef.current !== null) {
          const executionTime = performance.now() - queryStartTimeRef.current

          Sentry.captureMessage('DQL query executed', {
            level: executionTime > 5000 ? 'warning' : 'info',
            extra: {
              executionTime,
              docsReturned: newData?.items?.length || 0,
            },
          })

          queryStartTimeRef.current = null
        }

        if (activeView === 'editor') {
          addQueryToHistory({
            appId: props?.appId || '',
            query: query || '',
            createdAt: new Date().getTime(),
            lastUsed: new Date().getTime(),
            isFavorite: false,
            activeView,
          })
        }

        // Show toast notification for data-modifying queries
        if (query && isDataModifyingQuery(query)) {
          // Check if there was an error in the response by checking if error object has any keys
          if (newData?.error && Object.keys(newData.error).length > 0) {
            toast.error(
              `Query failed: ${newData.error.description || 'Unknown error'}`,
            )
          } else {
            // Show success toast with mutation count if available
            const mutatedCount = newData?.mutatedDocumentIds?.length || 0
            if (mutatedCount > 0) {
              toast.success(
                `Successfully modified ${mutatedCount} document${mutatedCount > 1 ? 's' : ''}`,
              )
            } else {
              toast.success('Query executed successfully')
            }
          }
        } else {
          // Only save the result for non-modifying queries
          // Update both ref (immediate) and state (async) to prevent race conditions
          previousResultRef.current = newData
          setPreviousResult(newData)
        }
      },
    },
  )

  /**
   * Update activeRow when data changes to ensure it reflects the latest data
   */
  useEffect(() => {
    setActiveRow((currentActiveRow) => {
      if (!currentActiveRow || !data?.items) {
        return currentActiveRow
      }

      // Find the updated version of the active row in the new data
      const updatedRow = data.items.find(
        (item) => item._id === currentActiveRow._id,
      )

      // If we found the row and it's different from the current one, update it
      if (updatedRow && updatedRow !== currentActiveRow) {
        return updatedRow
      }

      // If the row no longer exists in the new data, clear the selection
      if (!updatedRow) {
        return undefined
      }

      return currentActiveRow
    })
  }, [data])

  /**
   * Clean up the previousResultRef on unmount to prevent memory leaks
   */
  useEffect(() => {
    return () => {
      previousResultRef.current = undefined
    }
  }, [])

  /**
   * An effect to set the query to the initial query whenever it changes
   */
  useEffect(() => {
    if (initialQuery) {
      // Make sure to execute the initial query
      setQuery(initialQuery, true)
    }
  }, [initialQuery, props?.appCollections])

  /**
   * An effect to log an access event whenever the query changes
   */
  useEffect(() => {
    if (!query || !logDataAccessedEvent || !shouldExecute) {
      return
    }

    const isUnreported = !reportedRef.current[query]
    if (isUnreported) {
      reportedRef.current[query] = true
      // Note: technically this query _can_ be a mutation... But, without a parser or
      // some brittle regex, at least when considering better complex query support down
      // the road, we can't know which.
      logDataAccessedEvent({
        description: 'A user executed a DQL query',
        query,
      })
    }

    // Reset shouldExecute for next time
    setShouldExecute(true)
  }, [query, logDataAccessedEvent, shouldExecute])

  const queryError = data?.error
  const errorMessage = useMemo(() => {
    if (queryError?.description) {
      return queryError.description
    }

    if (serviceError instanceof Error) {
      return serviceError.message
    } else if (!!serviceError) {
      // NOTE: at this point we would have already reported the error to Sentry,
      // so we don't need to do it again.
      return 'An unknown error occurred'
    }
  }, [serviceError, queryError])

  const shouldSaveResult = useMemo(() => {
    return !isDataModifyingQuery(query || '')
  }, [query])

  return (
    <DQLEditorContext.Provider
      value={{
        result: shouldSaveResult
          ? data
          : (previousResult ?? previousResultRef.current),
        // Loading state gets stuck on true if the query is not enabled, a bit unintuitive.
        isLoading: shouldSaveResult
          ? isLoading && fetchStatus !== 'idle'
          : false,
        errorMessage,
        accessToken,
        query,
        setQuery,
        refetch,
        refetchInterval,
        setRefetchInterval,
        tailwind,
        sideBarOpen,
        setSideBarOpen,
        historySideBarOpen,
        setHistorySideBarOpen,
        showLatestTour,
        setShowLatestTour,
        logDataAccessedEvent,
        activeView,
        setActiveView,
        activeRow,
        setActiveRow,
        ...props,
      }}
    >
      {children}
    </DQLEditorContext.Provider>
  )
}
